import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useForm } from 'react-hook-form'
import { fetchSalesDiscount, fetchSalesAccountsReceivable, updateSalesDiscount } from '@/apis/aipass'
import { SettingDiscountEditForm } from '@/components/organisms/settings/sales-manager/discount/edit-form'
import { DiscountSettingType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { EditFooter } from '@/components/organisms/edit-footer'
import { useDiscountReceivableTargetChangeConfirmDialog } from '@/components/organisms/settings/sales-manager/use-discount-receivable-target-change-confirm-dialog'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

type DiscountEditFormValues = {
  discountSettings: DiscountSettingType[]
}

export const SettingSalesManagerDiscountEdit: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const { openConfirmDialog, DiscountReceivableTargetChangeConfirmDialog } = useDiscountReceivableTargetChangeConfirmDialog()
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<DiscountEditFormValues>({
    defaultValues: {
      discountSettings: [],
    },
    mode: 'all',
  })
  const [accountsReceivableSettings, setAccountsReceivableSettings] = useState<AccountsReceivableSettingType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onSaveDiscountSettings = async (formValues: DiscountEditFormValues) => {
    const isConfirmed = await openConfirmDialog()
    if (!isConfirmed) {
      return
    }
    try {
      setIsLoading(true)
      await updateSalesDiscount(formValues.discountSettings)
      history.push({ pathname: '/setting/sales-manager/discount' })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const _fetchSalesDiscount = async () => {
    setIsLoading(true)
    const res = await fetchSalesDiscount()
    reset({
      discountSettings: res?.salesDiscount || [],
    })
    setIsLoading(false)
  }

  const _fetchSalesAccountsReceivable = async () => {
    setIsLoading(true)
    const res = await fetchSalesAccountsReceivable()
    setAccountsReceivableSettings(res?.accountsReceivables || [])
    setIsLoading(false)
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/sales-manager/discount',
    onAllowed: () => {
      _fetchSalesDiscount()
      _fetchSalesAccountsReceivable()
    },
  })

  const footerContent = <EditFooter onSave={handleSubmit(onSaveDiscountSettings)} onCancel={history.goBack} isDisabledSave={!isValid} />

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <>
        <div css={settingDiscountEditContainerStyle}>
          <div css={settingDiscountEditHeaderStyle}>
            <p>{t('Discount')}</p>
          </div>
          <SettingDiscountEditForm control={control} accountsReceivableSettings={accountsReceivableSettings} />
        </div>
        <DiscountReceivableTargetChangeConfirmDialog />
      </>
    </SettingsLayout>
  )
}

const settingDiscountEditContainerStyle = css({
  height: '100%',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 5,
  background: '#FFF',
})

const settingDiscountEditHeaderStyle = css({
  height: 50,
  padding: '16px 32px',
  borderBottom: '1px solid #F2F2F2',
  p: {
    fontSize: 18,
    fontWeight: 'bold',
  },
})
